import { useEffect, useState } from "react";
import { useSearchResultList } from "./SearchResultList.hooks.ts";
import { DocumentSearchResponse, DocumentSearchResult, SearchFilterType } from "./SearchResultList.types.ts";
import { useFeatureFlags, usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { FeatureFlag } from "../../../auth/planProvider/PlanProvider.types.ts";
import { useDelayedValue } from "../../hooks/useDebounce.hooks.ts";
import { useDotLegalQueryArray } from "@common/hooks/dotLegalReactQuery.ts";

const minQueryLength = 2;

const useDocumentSearch = (debouncedQuery: string, page: number, isSearchIndexingEnabled: boolean) => {
    const shouldFetch = debouncedQuery.length >= minQueryLength && isSearchIndexingEnabled;

    return useDotLegalQueryArray<DocumentSearchResponse>(
        ["documentSearch", debouncedQuery, page],
        `/documents/search?query=${encodeURIComponent(debouncedQuery)}&page=${page}`,
        { enabled: shouldFetch, cacheTime: 15000 }
    );
};

export const useGlobalSearchResultList = (fetchUsers: boolean) => {
    const entitySearchHook = useSearchResultList(fetchUsers);
    const [currentQuery, setCurrentQuery] = useState<string>("");
    const [filter, setFilter] = useState<SearchFilterType>("system");
    const [page, setPage] = useState<number>(1);
    const [accumulatedResults, setAccumulatedResults] = useState<Array<DocumentSearchResult>>([]);
    const [lastProcessedPage, setLastProcessedPage] = useState<number>(0);
    const [lastDebouncedQuery, setLastDebouncedQuery] = useState<string>("");
    const { addonsFeatures } = usePlanContext();

    const debouncedQuery = useDelayedValue(currentQuery, 500);

    const { data: documentSearchData, isLoading: documentSearchIsLoading, isFetching } = useDocumentSearch(debouncedQuery, page, addonsFeatures.searchService);

    useEffect(() => {
        if (debouncedQuery !== lastDebouncedQuery) {
            setPage(1);
            setLastProcessedPage(0);
            setAccumulatedResults([]);
            setLastDebouncedQuery(debouncedQuery);
        }
    }, [debouncedQuery, lastDebouncedQuery]);

    useEffect(() => {
        if (documentSearchData?.results && !isFetching && page !== lastProcessedPage) {
            if (page === 1) {
                setAccumulatedResults(documentSearchData.results);
            } else {
                setAccumulatedResults((prev) => [...prev, ...documentSearchData.results]);
            }
            setLastProcessedPage(page);
        }
    }, [documentSearchData, page, isFetching, lastProcessedPage]);

    const fetchSuggestions = async (query: string) => {
        setCurrentQuery(query);
        if (query.length >= minQueryLength) {
            await entitySearchHook.fetchSuggestions(query);
        }
    };

    const documentResults = accumulatedResults;
    const entitySearchCount = entitySearchHook.entitySearchSuggestions.length;
    const totalDocumentCount = documentSearchData?.totalCount;
    const hasMoreDocuments = documentSearchData?.hasMoreResults || false;

    let entitySearchSuggestions = entitySearchHook.entitySearchSuggestions;
    if (currentQuery.length < minQueryLength) {
        entitySearchSuggestions = [];
    }

    const loadMoreDocuments = () => {
        setPage((prevPage) => prevPage + 1);
    };

    return {
        entitySearchSuggestions: entitySearchSuggestions,
        onShowMoreForEntityTypeClick: entitySearchHook.onShowMoreForEntityTypeClick,
        isEntitySpecificSearch: entitySearchHook.isEntitySpecificSearch,
        setShowMoreForEntityType: entitySearchHook.setShowMoreForEntityType,
        entitySearchIsLoading: entitySearchHook.isLoading,
        documentSearchIsLoading,
        fetchEntitySearchSuggestions: fetchSuggestions,
        documentResults,
        filter,
        setFilter,
        entitySearchCount,
        totalDocumentCount,
        hasMoreDocuments,
        loadMoreDocuments,
        minQueryLength,
    };
};
